import CardGiftcardOutlined from "@mui/icons-material/CardGiftcardOutlined";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import IconButton from "@mui/material/IconButton";
import { useState } from "react";
import type { ThankYouNote } from "@/interfaces/thankYouNotes";
import { formatDateLong } from "@/utils/date-format";
import "./thankYouNoteCard.css";

interface ThankYouNoteRowProps {
  note: ThankYouNote;
  isFavorite: boolean;
  onToggleFavorite: (id: number) => void;
}

export default function ThankYouNoteRow({
  note,
  isFavorite,
  onToggleFavorite,
}: ThankYouNoteRowProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const [lastGiftImg, setLastGiftImg] = useState(note.giftImg);
  // Reset the failure flag when this row starts rendering a different note's image, without a
  // useEffect: adjust state during render per the React docs pattern for derived state.
  if (note.giftImg !== lastGiftImg) {
    setLastGiftImg(note.giftImg);
    setImgFailed(false);
  }

  const metaLine = [
    note.recipient,
    note.campaign,
    `Submitted ${formatDateLong(note.submitted)}`,
    `Redeemed ${formatDateLong(note.redeemed)}`,
    note.giftName ?? "—",
  ].join(" · ");

  return (
    <div className="flex items-center gap-[14px] border border-smoke rounded-[10px] bg-white py-3 px-4">
      <div className="ty-row-thumb bg-wax flex items-center justify-center">
        {/* giftImg can fail to load even once GFG-234 wires it to a real Shopify CDN URL
            (product removed, file swapped, image expired) — fall back to the same placeholder
            used for a null image. */}
        {!note.giftImg || imgFailed ? (
          <CardGiftcardOutlined className="text-rodeo-dust" style={{ fontSize: "22px" }} />
        ) : (
          <img
            src={note.giftImg}
            alt={note.giftName ?? "Gift"}
            className="w-full h-full object-cover block"
            onError={() => setImgFailed(true)}
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-crimson italic text-[15px] text-rodeo-black leading-[1.4]">
          &ldquo;{note.message}&rdquo;
        </div>
        <div className="text-[12px] text-graphite mt-[3px] font-lato">{metaLine}</div>
      </div>

      <IconButton
        aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
        aria-pressed={isFavorite}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          onToggleFavorite(note.id);
        }}
        sx={{
          color: isFavorite ? "var(--color-dark-dust)" : "var(--color-rodeo-dust)",
        }}
      >
        {isFavorite ? (
          <FavoriteIcon sx={{ fontSize: 18 }} />
        ) : (
          <FavoriteBorderIcon sx={{ fontSize: 18 }} />
        )}
      </IconButton>
    </div>
  );
}
